import { isEmptyHtml } from 'util/is_empty_html'

import { MEDIA_QUERIES } from 'design'
import usePreviousProps from 'hooks/previous_props_hook'
import { useTrixLocalStorage } from 'hooks/trix/trix_local_storage_hook'
import { useTrixFileUploading } from 'hooks/trix/use_trix_file_uploading'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import ErrorBoundary from '../../containers/error_boundary'
import { setSelectedTimelineId } from '../../reduxes/slices/selected/actions'
import { getSelectedTimelineId } from '../../reduxes/slices/selected/selectors'
import { getMyLatestChatTimeline } from '../../reduxes/slices/timelines/selectors'
import { useLocalStorage } from '../use_local_storage'
import { useMediaQuery } from '../use_media_query'
import { useTrixMentions } from './use_trix_mentions'

export type UseTrixEditorProps = {
  handleEditorReady: (event: any) => void
  handleChange: (event: any) => void
  handleKeyDown: (event: any) => void
  handleSubmit: (event?: any) => Promise<any>
  isSubmitDisabled: boolean
  handleEnterToSend: () => void
  enterToSend: boolean
  editor: any
  focusAtStart: () => void
  isRecording: boolean
  setIsRecording: React.Dispatch<React.SetStateAction<boolean>>
  clearEditorContent: () => void
}

type Props = {
  id: string
  onChange?: ((value: any) => void) | undefined
  onSubmit: (html: any) => Promise<any>
  trixHtml?: any
  persistToLS?: boolean
  enterToSendProp?: boolean
  clearAfterSend?: boolean
}

const emptyHtmlRegex =
  /(?:&nbsp;| |<br>|<\/*div>|<\/*em>|<\/*h1>|<\/*strong>|<\/*ul>|<\/*ol>|<\/*li>|<\/*pre>)/g

export const useTrixEditor = ({
  id,
  onChange,
  onSubmit,
  trixHtml,
  persistToLS = true,
  enterToSendProp = false,
  clearAfterSend = true,
}: Props): UseTrixEditorProps => {
  const prevId = usePreviousProps(id)
  const idRef = useRef<string>(id)
  const editorRef = useRef<any>()
  const [editorReady, setEditorReady] = useState<boolean>(false)
  const [htmlValues, setHtmlValues] = useState<any>({})
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [enterToSend, setEnterToSend] = useState<boolean>(enterToSendProp)
  const [isRecording, setIsRecording] = useState<boolean>(false)

  const dispatch = useDispatch()
  const latestTimeline = useSelector(getMyLatestChatTimeline)
  const selectedTimelineId = useSelector(getSelectedTimelineId)
  const ls = useLocalStorage()

  const desktop = useMediaQuery(MEDIA_QUERIES.md)
  const {
    editorMetaData,
    getEditorFromStorage,
    saveEditorToStorage,
    deleteEditorInStorage,
  } = useTrixLocalStorage(editorRef, persistToLS)

  const {
    noOfUploadingAttachments,
    handleAttachmentAdd,
    handleDrop,
    handleFileAccept,
  } = useTrixFileUploading(
    id,
    editorRef.current,
    trixHtml,
    getEditorFromStorage,
    saveEditorToStorage,
  )

  useTrixMentions(id, editorRef)

  const currentHtmlValue = htmlValues[id]
  const prevHtmlValue = usePreviousProps(currentHtmlValue)

  // Determine if the current Html is empty, or not. Matches provided Html parts related to empty messages
  const checkCurrentHtmlEmptyValue = currentHtmlValue?.replace(
    emptyHtmlRegex,
    '',
  )

  const isSubmitDisabled =
    !currentHtmlValue ||
    !checkCurrentHtmlEmptyValue ||
    !!noOfUploadingAttachments ||
    isSubmitting ||
    isRecording

  const focusAtStart = useCallback(() => {
    if (desktop) {
      editorRef.current?.setSelectedRange(0)
    }
  }, [desktop])

  const focusAtEnd = useCallback(() => {
    const length = editorRef.current.getDocument().getLength()
    editorRef.current.setSelectedRange([length - 1, length])
  }, [])

  useEffect(() => {
    if (!editorReady) return

    focusAtStart()
    window.addEventListener('drop', handleDrop, false)
    editorRef.current?.element.addEventListener(
      'trix-attachment-add',
      handleAttachmentAdd,
      false,
    )
    window.addEventListener('trix-file-accept', handleFileAccept, false)

    setEditorReady(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorReady])

  useEffect(() => {
    if (!selectedTimelineId) {
      focusAtStart()
    }
  }, [focusAtStart, selectedTimelineId])

  useEffect(() => {
    const cleanup = () => {
      if (editorRef.current) saveEditorToStorage(idRef.current)
    }

    setEnterToSend(ls.getItem('enterToSend') !== 'false' || false)

    window.addEventListener('beforeunload', cleanup)
    return () => {
      window.removeEventListener('beforeunload', cleanup)
      cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (editorRef.current) saveEditorToStorage(idRef.current)
  }, [saveEditorToStorage, desktop])

  useEffect(() => {
    if (!editorRef.current && !editorReady) {
      return
    }

    const editorToLoad = getEditorFromStorage(id)
    if (editorToLoad) {
      focusAtStart()
      editorRef.current.loadJSON(editorToLoad)
      const editorData = (editorMetaData as any)[id]
      if (editorData) {
        const editorAttachmentData = editorData['attachments']
        const doc = editorRef.current.getDocument()
        doc.getAttachments().forEach((attachment: any) => {
          const attachmentData =
            editorAttachmentData[attachment.attributes.values.prevId]
          attachment.setPreviewURL(attachmentData?.previewURL)
        })
      }
      deleteEditorInStorage(id)
    } else if (trixHtml) {
      editorRef.current.loadHTML(trixHtml)
      focusAtEnd()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorReady, trixHtml])

  useEffect(() => {
    if (editorRef.current && prevId && prevId !== id) {
      idRef.current = id
      if (!prevHtmlValue && !editorRef.current.composition.attachments.length) {
        deleteEditorInStorage(prevId)
      } else {
        saveEditorToStorage(prevId)
      }
    }

    return () => {
      window.removeEventListener('drop', handleDrop, false)
      editorRef.current?.element.removeEventListener(
        'trix-attachment-add',
        handleAttachmentAdd,
        false,
      )
      window.removeEventListener('trix-file-accept', handleFileAccept, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleEditorReady = useCallback((editor: any) => {
    editorRef.current = editor
    setEditorReady(true)
  }, [])

  const handleChange = useCallback(() => {
    if (!editorRef.current) {
      return
    }

    const html = editorRef.current.element.value
    setHtmlValues((prevHtmlValue: any) => {
      return { ...prevHtmlValue, [id]: isEmptyHtml(html) ? '' : html }
    })
    onChange && onChange(html)
  }, [id, onChange])

  const clearEditorContent = useCallback(() => {
    editorRef.current.setSelectedRange([
      0,
      editorRef.current.getDocument().getLength(),
    ])
    editorRef.current.deleteInDirection('backward')
  }, [])

  // This handler no longer stops event propagation - this is handled closer to the element
  // This is due to the differences between desktop and mobile
  const handleSubmit = useCallback(() => {
    if (isSubmitDisabled) {
      editorRef.current.element.contentEditable = 'true'
      return Promise.reject()
    }

    setIsSubmitting(true)

    // trix is weird the API looks like this so in order to remove this stuff
    // without getting really deep in the weeds add custom rules here
    return onSubmit(currentHtmlValue.replace(rgxLeadTrailBR, ''))
      .then(() => {
        if (clearAfterSend) clearEditorContent()
        deleteEditorInStorage(id)
        return Promise.resolve()
      })
      .catch((e) => {
        ErrorBoundary.logErrorToMyService(e)
        return Promise.reject()
      })
      .finally(() => {
        editorRef.current.element.contentEditable = 'true'
        focusAtStart()
        setIsSubmitting(false)
      })
  }, [
    clearAfterSend,
    clearEditorContent,
    currentHtmlValue,
    deleteEditorInStorage,
    focusAtStart,
    id,
    isSubmitDisabled,
    onSubmit,
  ])

  const handleEnterToSend = useCallback(() => {
    ls.setItem('enterToSend', !enterToSend ? 'true' : 'false')
    setEnterToSend((previous) => !previous)
  }, [enterToSend, ls])

  const handleKeyDown = useCallback(
    (event: any) => {
      if (event.keyCode === 13) {
        if (
          (!(event.ctrlKey || event.metaKey) &&
            enterToSend &&
            !event.shiftKey) ||
          (!enterToSend && event.shiftKey)
        ) {
          event.preventDefault()
          if (currentHtmlValue !== '') {
            editorRef.current.element.contentEditable = 'false'
            handleSubmit()
          }
        } else if (event.ctrlKey || event.metaKey) {
          event.preventDefault()
          handleEnterToSend()
        }
      } else {
        if (
          event.keyCode === 38 &&
          !currentHtmlValue &&
          !(noOfUploadingAttachments > 0)
        ) {
          if (latestTimeline) dispatch(setSelectedTimelineId(latestTimeline.id))
        }
        if (event.keyCode === 27) {
          dispatch(setSelectedTimelineId(null))
        }
      }
    },
    [
      currentHtmlValue,
      dispatch,
      enterToSend,
      handleEnterToSend,
      handleSubmit,
      latestTimeline,
      noOfUploadingAttachments,
    ],
  )

  return {
    handleEditorReady,
    handleChange,
    handleKeyDown,
    handleSubmit,
    isSubmitDisabled,
    handleEnterToSend,
    enterToSend,
    editor: editorRef.current,
    focusAtStart,
    isRecording,
    setIsRecording,
    clearEditorContent,
  }
}

const rgxLeadTrailBR =
  /(<div>)((&nbsp;\s*)*<br>)+|(<br>(&nbsp;\s*)*)+(<\/div>)/g
